import { useEffect, useRef } from 'react';

import { Map as MapLibreMap } from 'maplibre-gl';

import { REGION_BORDER, REGION_FILL, REGION_HOVER, REGION_LABEL } from '../CountryView/constants';
import { Aggregated } from '../CountryView/types';
import { flyToCountryBounds } from '../CountryView/utils/flyToCountryBounds';
import { computeBounds } from '../MapSharedData';

// Layer ids declared by sibling unified hooks / UnifiedMap. Kept as local
// constants so this hook has no compile-time coupling to those modules.
const WORLD_FILL_LAYER = 'countries-fill';
const WORLD_LABEL_LAYER = 'countries-label';
const OUTLINE_LAYER = 'country-outline-line';

// Big countries can't be framed at minZoom 3 (Canada needs ~2.3). On the
// unified map we adjust minZoom dynamically at drill-in — the persistent
// instance starts at minZoom 1 (world). Same set as the legacy useMapLibre.
const LARGE_COUNTRIES = new Set(['CA', 'RU', 'US', 'AU', 'CN', 'BR', 'KZ', 'AR', 'IN']);

const CROSSFADE_DURATION = 600;
const FLY_DURATION = 1100;

interface UseUnifiedDrillInOptions {
  mapRef: React.MutableRefObject<MapLibreMap | null>;
  ready: boolean;
  activeCountry: string | null;
  /** Region polygons for the active country (already loaded). */
  geojson: GeoJSON.FeatureCollection | null;
  aggregated: Aggregated;
  /** Country that `aggregated` was computed for — used as a freshness guard. */
  aggregatedCountry: string | null;
  regionNameProperty: string;
  /** Total incidents in the active country. 0 means we can fly without
   *  waiting for aggregation (fall back to the country's full bounds). */
  incidentsCount: number;
}

/**
 * Drill-in orchestration for REFACTOR_PLAN.md step 3.
 *
 * When activeCountry transitions to a country with loaded geojson + fresh
 * aggregation, this hook performs the full crossfade + fly sequence on the
 * persistent map instance:
 *   1. Adjust minZoom for the country (Math.min(desired, currentZoom) so
 *      we never clamp the handoff viewport upward).
 *   2. Configure fill/line-opacity transitions to 600 ms.
 *   3. Flip the country-level layers' visibility from 'none' to 'visible'
 *      (visibility itself isn't interpolated — the opacity transition is).
 *   4. Animate world fill → 0 and region fill/border/outline up to their
 *      target opacities. Region label rides its own zoom-based text-opacity.
 *   5. flyToCountryBounds to the active regions (or full country if none).
 *
 * The hook is idempotent per country — re-running after already drilling
 * into the same country is a no-op. A ref tracks the last drilled country
 * and resets when the user returns to the world view (step 4 handles the
 * visual restoration).
 */
export function useUnifiedDrillIn({
  mapRef,
  ready,
  activeCountry,
  geojson,
  aggregated,
  aggregatedCountry,
  regionNameProperty,
  incidentsCount,
}: UseUnifiedDrillInOptions) {
  const drilledForRef = useRef<string | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    // World view — visual reset is owned by drill-out (step 4). Here we
    // just clear the "drilled for" tracker so subsequent re-entries fire.
    if (!activeCountry) {
      drilledForRef.current = null;
      return;
    }

    // Already drilled into this country; nothing to do.
    if (drilledForRef.current === activeCountry) return;

    // Need the regions geojson for the active country to compute the fly target.
    if (!geojson) return;

    // Wait for aggregation to catch up unless there are no incidents at all
    // (then there's nothing to wait for and we frame the whole country).
    if (incidentsCount > 0 && aggregatedCountry !== activeCountry) return;

    const activeFeatures = geojson.features.filter((f) => {
      const name = f.properties?.[regionNameProperty];
      return name && name !== 'Unassigned' && (aggregated[name]?.total ?? 0) > 0;
    });
    const target =
      activeFeatures.length > 0
        ? computeBounds({ type: 'FeatureCollection', features: activeFeatures })
        : computeBounds(geojson);

    if (!target) return;
    drilledForRef.current = activeCountry;

    // 1. minZoom — clamp to current zoom so handoff from world (~1.5) doesn't
    //    snap upward. Big countries get 2, others 3.
    const currentZoom = map.getZoom();
    const desiredMinZoom = LARGE_COUNTRIES.has(activeCountry) ? 2 : 3;
    map.setMinZoom(Math.min(desiredMinZoom, currentZoom));

    // 2. Transitions on the layers we're about to animate. Setting these
    //    BEFORE the value change is what makes MapLibre interpolate.
    map.setPaintProperty(WORLD_FILL_LAYER, 'fill-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });
    map.setPaintProperty(REGION_FILL, 'fill-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });
    map.setPaintProperty(REGION_BORDER, 'line-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });
    map.setPaintProperty(OUTLINE_LAYER, 'line-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });
    // World country labels (the names that float over neighbours at country
    // zoom) clutter the country view — fade them out alongside the fill.
    map.setPaintProperty(WORLD_LABEL_LAYER, 'text-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });

    // 3. Show the country-level layers (added with visibility 'none' in step 2).
    //    REGION_HOVER renders the primary-color overlay through feature-state,
    //    so it has to be visible too — otherwise hovering a region does
    //    nothing visually even though setFeatureState fires.
    //    REGION_DIM/HIGHLIGHT and PC_* are managed elsewhere.
    for (const id of [REGION_FILL, REGION_BORDER, REGION_LABEL, REGION_HOVER]) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'visible');
    }

    // 4. Crossfade — world fades to 0, regions / border / outline fade up.
    map.setPaintProperty(WORLD_FILL_LAYER, 'fill-opacity', 0);
    map.setPaintProperty(WORLD_LABEL_LAYER, 'text-opacity', 0);
    map.setPaintProperty(REGION_FILL, 'fill-opacity', 0.75);
    map.setPaintProperty(REGION_BORDER, 'line-opacity', 1);
    map.setPaintProperty(OUTLINE_LAYER, 'line-opacity', 0.85);

    // 5. Camera. flyToCountryBounds handles the antimeridian fallback for
    //    USA/RU/etc. and uses easeTo for the normal case.
    flyToCountryBounds({
      map,
      bounds: target,
      countryCode: activeCountry,
      duration: FLY_DURATION,
    });
  }, [
    mapRef,
    ready,
    activeCountry,
    geojson,
    aggregated,
    aggregatedCountry,
    regionNameProperty,
    incidentsCount,
  ]);
}
